import { useTranslation } from '../presentation/common/useTranslation.js';
import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { logoutUser } from '../services/api/auth';

import { ScanBarcode, Tag, Settings, LogOut, Store, Menu, X, Sword, History } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import '../styles/world.css';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { useCurrentUser } from '../hooks/useCurrentUser';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function PartnerLayout() {
  const { t } = useTranslation();
  const tr = (key, options) => t(`partner_portal.${key}`, options);
  const navigate = useNavigate();
  const { user, isAdventurer } = useCurrentUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: tr('pos'), href: '/partner/pos', icon: ScanBarcode },
    { name: tr('manage_offers'), href: '/partner/offers', icon: Tag },
    { name: tr('redemptions', { defaultValue: 'Einlösungshistorie' }), href: '/partner/redemptions', icon: History },
    { name: tr('my_profile'), href: '/partner/profile', icon: Store },
    { name: tr('settings'), href: '/partner/settings', icon: Settings },
  ];

  return (
    <div className="portal-shell portal-partner flex h-dvh overflow-hidden bg-zinc-950">
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <nav className={cn(
        "portal-sidebar w-72 md:w-64 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 md:translate-x-0 md:static md:flex h-full shadow-2xl md:shadow-none",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="portal-brand p-5 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Store className="w-6 h-6 text-amber-500" />
              <h1 className="text-lg font-semibold text-zinc-100 tracking-tight">
                {tr('brand_title')}
              </h1>
            </div>
            <button 
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 md:hidden"
              aria-label={t('dialogs.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-zinc-500">{tr('b2b_dashboard')}</p>
        </div>

        <div className="partner-nav-links flex-1 min-h-0 px-3 space-y-1 mt-2 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                cn(
                  'portal-nav-link flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm font-medium transition-colors rounded-lg',
                  isActive
                    ? 'bg-zinc-800 text-zinc-100 border-l-2 border-amber-500 font-semibold'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent'
                )
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{item.name}</span>
            </NavLink>
          ))}
        </div>

        {/* Dual Role switch to Adventurer if user has Adventurer role */}
        {isAdventurer && (
          <div className="px-3 pb-2 pt-2 border-t border-zinc-800/60">
            <button
              onClick={() => {
                navigate('/dashboard');
                setSidebarOpen(false);
              }}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 min-h-[44px] text-xs font-bold text-amber-400 hover:text-amber-300 bg-amber-950/30 hover:bg-amber-950/50 border border-amber-800/50 rounded-lg transition-colors"
            >
              <Sword className="w-4 h-4 shrink-0" />
              <span>{t('nav_cross.goto_adventurer_dashboard', 'Zum Abenteurer-Portal')}</span>
            </button>
          </div>
        )}

        <div className="p-4 mt-auto border-t border-zinc-800 space-y-3">
          <div className="flex items-center justify-between gap-2 px-1">
            <LanguageSwitcher />
            <ThemeSwitcher variant="toggle" />
          </div>

          <div className="flex items-center justify-between pt-1">
            {user && (
              <p className="text-xs text-zinc-400 truncate max-w-[140px]" title={user.username}>{user.username}</p>
            )}
            <button 
              onClick={async () => {
                await logoutUser();
                setSidebarOpen(false);
                navigate('/', { replace: true });
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 rounded transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>{t('common.login', { defaultValue: 'Abmelden' }) === 'Log In' ? 'Log Out' : t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="portal-main flex-1 flex flex-col h-full min-w-0 overflow-hidden">
        <header className="portal-topbar relative z-30 h-16 shrink-0 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-zinc-400 hover:text-zinc-200 md:hidden transition-colors rounded-lg hover:bg-zinc-800"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <span className="font-serif font-bold text-base text-zinc-100 hidden sm:inline">{tr('brand_title')}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeSwitcher variant="toggle" />
            </div>

            <div className="text-right">
              <p className="text-xs sm:text-sm font-medium text-zinc-200 truncate max-w-[120px] sm:max-w-none">{tr('console_title')}</p>
              <p className="text-[10px] sm:text-xs text-amber-500 font-semibold">{tr('partner_view')}</p>
            </div>
          </div>
        </header>

        <div data-page-scroll className="portal-content flex-1 min-h-0 overflow-y-auto p-3 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
